import type React from 'react';
import styled from 'styled-components';

import PlayIcon from 'app/assets/play-icon.svg';
import PauseIcon from 'app/assets/pause-icon.svg';
import RestartIcon from 'app/assets/restart-icon.svg';

type TimerControlsProps = {
  className?: string;
  isRunning: boolean;
  play: () => void;
  pause: () => void;
  restart: () => void;
  timerEnabled: boolean;
};

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 100%;
`;

const getBackgroundColor = ({
  theme,
  $isPrimary,
  timerEnabled,
}: { theme: any; $isPrimary: boolean; timerEnabled: boolean }) => {
  if ($isPrimary && timerEnabled) {
    return theme.colors.white;
  }
  if (!timerEnabled) {
    return theme.colors.blackSwan;
  }
  return theme.colors.veryGray;
};

const ControlButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'timerEnabled',
})<{ $isPrimary: boolean; timerEnabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${getBackgroundColor};
  border: none;
  border-radius: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  height: 50%;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ControlButtonIcon = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'timerEnabled',
})<{ timerEnabled: boolean }>`
  width: ${({ theme }) => theme.spacing(8)};
  height: ${({ theme }) => theme.spacing(8)};
  filter: ${({ timerEnabled }) => (!timerEnabled ? 'brightness(0.6) saturate(0.3)' : 'none')};
  opacity: ${({ timerEnabled }) => (!timerEnabled ? 0.7 : 1)};
`;

const TimerControls: React.FC<TimerControlsProps> = ({ className, isRunning, play, pause, restart, timerEnabled }) => {
  const handleTogglePlayPause = () => (isRunning ? pause() : play());

  return (
    <ControlsWrapper className={className} data-testid="timer-controls">
      <ControlButton
        onClick={handleTogglePlayPause}
        $isPrimary={true}
        timerEnabled={timerEnabled}
        disabled={!timerEnabled}
      >
        <ControlButtonIcon
          src={isRunning ? PauseIcon : PlayIcon}
          alt={isRunning ? 'Pause' : 'Play'}
          timerEnabled={timerEnabled}
        />
      </ControlButton>
      <ControlButton onClick={restart} $isPrimary={false} timerEnabled={timerEnabled} disabled={!timerEnabled}>
        <ControlButtonIcon src={RestartIcon} alt="Restart" timerEnabled={timerEnabled} />
      </ControlButton>
    </ControlsWrapper>
  );
};

export default TimerControls;
